/**
 * The navigation icons, as inline SVG.
 *
 * Inline rather than an icon library. The rail needs about ten glyphs, and the smallest icon package
 * that covers them ships a few thousand — a dependency whose surface is ninety-nine per cent unused,
 * and one more thing to audit for a bank.
 *
 * Every icon is drawn on a 24×24 grid with `currentColor` and a 1.75 stroke, so they inherit the
 * nav item's colour and match the text weight beside them. `aria-hidden` on all of them: the label
 * next to the icon is the accessible name, and announcing both reads the destination twice.
 */

import type { SVGProps } from "react";

type IconProps = Omit<SVGProps<SVGSVGElement>, "children">;

function Glyph({ children, ...rest }: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      // Sized in `em` so an icon tracks the font size of the item it sits in rather than needing a
      // second size token per context.
      className="h-[1.15em] w-[1.15em] shrink-0"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** The assistant. A speech bubble. */
export const ChatIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </Glyph>
);

/** Past conversations. A clock turned back. */
export const HistoryIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
    <path d="M12 7v5l4 2" />
  </Glyph>
);

/** Drafting a reply. A pen over a page. */
export const DraftsIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Glyph>
);

/** The corpus. Stacked documents. */
export const KnowledgeIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </Glyph>
);

/** Supervision. An eye over the record. */
export const SupervisionIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </Glyph>
);

/** Analytics. Bars. */
export const AnalyticsIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M18 20V10" />
    <path d="M12 20V4" />
    <path d="M6 20v-6" />
  </Glyph>
);

/** Employee accounts. Two figures. */
export const UsersIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Glyph>
);

/** A new conversation. A plus. */
export const PlusIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Glyph>
);

/** Signing out. A door with an arrow. */
export const SignOutIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </Glyph>
);

/** The bank. A classical façade, which is the assistant's own mark. */
export const BankIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M3 21h18" />
    <path d="M5 21V10l7-5 7 5v11" />
    <path d="M9 21v-6h6v6" />
  </Glyph>
);

// ---------------------------------------------------------------------------------------
// Added for the new design.
//
// The mockups use Material Symbols, loaded as a webfont. Drawn here instead, for the reason the
// module already gives and one more: Material Symbols Outlined is a variable font of some three
// thousand glyphs and several megabytes, downloaded on every page load so that about twenty of
// them can be rendered. On a branch workstation with no route to the public internet it downloads
// nothing at all and every icon renders as its ligature text — "content_copy" in place of the copy
// button, in the middle of the toolbar.
//
// Same 24×24 grid, same stroke, so they sit correctly beside the ten that were already here.

/** Search. */
export const SearchIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </Glyph>
);

/** The sources panel. Stacked books. */
export const SourcesIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <path d="M9 7h7" />
  </Glyph>
);

/** Attach a document. A paperclip. */
export const AttachIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </Glyph>
);

/** Send the message. A paper plane. */
export const SendIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M22 2L11 13" />
    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
  </Glyph>
);

/** Copy to the clipboard. Two overlapping sheets. */
export const CopyIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Glyph>
);

/** Helpful. */
export const ThumbUpIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    <path d="M7 11l4.5-8a2.5 2.5 0 0 1 4.7 1.6L15.5 9H20a2 2 0 0 1 2 2.4l-1.4 7A2 2 0 0 1 18.6 20H7z" />
  </Glyph>
);

/** Not helpful. The same glyph, turned. Drawn rather than CSS-rotated: `corePlugins.preflight` is
 *  off, which leaves every Tailwind transform utility a no-op — see tailwind.config.mjs. */
export const ThumbDownIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
    <path d="M17 13l-4.5 8a2.5 2.5 0 0 1-4.7-1.6L8.5 15H4a2 2 0 0 1-2-2.4l1.4-7A2 2 0 0 1 5.4 4H17z" />
  </Glyph>
);

/** An effective date. A calendar. */
export const CalendarIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
  </Glyph>
);

/** Open the rail on a narrow viewport. */
export const MenuIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </Glyph>
);

/** Dismiss a panel or drawer. */
export const CloseIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </Glyph>
);

/** Expand or collapse. Rendered pointing down; callers flip it by swapping in `ChevronUpIcon`. */
export const ChevronDownIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M6 9l6 6 6-6" />
  </Glyph>
);

export const ChevronUpIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M18 15l-6-6-6 6" />
  </Glyph>
);

/** Upload a document. */
export const UploadIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 9l5-5 5 5" />
    <path d="M12 4v12" />
  </Glyph>
);

/** A document in the corpus. */
export const DocumentIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </Glyph>
);

/** Filter a list. */
export const FilterIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M3 5h18l-7 8v6l-4 2v-8z" />
  </Glyph>
);

/** Informational, not a problem. */
export const InfoIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </Glyph>
);

/** A warning that needs a person. */
export const AlertIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M10.3 3.9L2.1 18a2 2 0 0 0 1.7 3h16.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </Glyph>
);

/** A guardrail fired. A shield. */
export const GuardrailIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </Glyph>
);

/** Open in a new place — the full session, the source document. */
export const ExternalLinkIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M14 4h6v6" />
    <path d="M20 4l-9 9" />
    <path d="M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
  </Glyph>
);

/** Further actions. Three dots, vertical. */
export const MoreIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="19" r="1" />
  </Glyph>
);

/** Dictate (UC-A6). A microphone. */
export const MicIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <path d="M12 18v4" />
  </Glyph>
);

/** Recording, and pressing again stops. A filled square, which is the universal stop. */
export const StopIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
  </Glyph>
);

/**
 * Read aloud (UC-A6). A speaker with waves.
 *
 * The waves are drawn on the inline-end side and the glyph is not mirrored in Arabic: a speaker
 * icon is a picture of an object rather than a directional cue, and flipping it makes it read as a
 * different control.
 */
export const SpeakerIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M18.5 5.5a9 9 0 0 1 0 13" />
  </Glyph>
);

/** Reading aloud now; pressing again stops. A speaker with the waves struck through. */
export const SpeakerOffIcon = (props: IconProps): JSX.Element => (
  <Glyph {...props}>
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="M22 9l-6 6" />
    <path d="M16 9l6 6" />
  </Glyph>
);
